import { cn } from "@/lib/cn";
import {
  motion,
  useMotionValueEvent,
  useTransform,
  type MotionValue,
} from "motion/react";
import { useId, useState } from "react";
import { LINE_ART } from "./lineArt";

type Props = {
  draw: MotionValue<number>;
  controlsOpacity: MotionValue<number>;
  className?: string;
};

const LIGHT_DELAY_MS = 1000;
const LIGHT_STAGGER_MS = 700;

export function BuildingLineArt({ draw, controlsOpacity, className }: Props) {
  const id = useId();
  const [lit, setLit] = useState(false);
  useMotionValueEvent(draw, "change", (progress) => {
    const previous = draw.getPrevious() ?? progress;
    if (previous < 1 && progress >= 1) {
      setLit(true);
    }
    if (previous >= 1 && progress < 1) {
      setLit(false);
    }
  });

  return (
    <svg
      viewBox={`0 0 ${LINE_ART.width} ${LINE_ART.height}`}
      fill="none"
      role="button"
      aria-pressed={lit}
      aria-label="Toggle the room lights"
      onClick={() => setLit((value) => !value)}
      className={cn("cursor-pointer", className)}
    >
      <defs>
        {LINE_ART.lights.map((light, index) => (
          <clipPath key={index} id={`${id}-clip-${index}`}>
            <rect {...light.clip} />
          </clipPath>
        ))}
        <filter
          id={`${id}-glow`}
          x="-100%"
          y="-100%"
          width="300%"
          height="300%"
        >
          <feGaussianBlur stdDeviation={LINE_ART.lights[0].shapes[0].blur} />
        </filter>
      </defs>
      {LINE_ART.lights.map((light, index) => (
        <g
          key={index}
          clipPath={`url(#${id}-clip-${index})`}
          className={cn(
            "transition-opacity duration-700 ease-in-out",
            lit ? "opacity-100" : "opacity-0",
          )}
          style={{
            transitionDelay: lit
              ? `${LIGHT_DELAY_MS + index * LIGHT_STAGGER_MS}ms`
              : "0ms",
          }}
        >
          {light.shapes.map((shape) => (
            <path
              key={shape.d}
              d={shape.d}
              fill={shape.fill}
              filter={`url(#${id}-glow)`}
            />
          ))}
        </g>
      ))}
      {LINE_ART.strokes.map((d, index) => (
        <Stroke key={index} d={d} draw={draw} index={index} />
      ))}
      {LINE_ART.controls.map((control, index) => (
        <motion.path
          key={index}
          d={LINE_ART.controlPath}
          transform={`translate(${control.x} ${control.y})`}
          fill="currentColor"
          style={{ opacity: controlsOpacity }}
        />
      ))}
    </svg>
  );
}

const STROKE_WINDOW = 0.12;

function Stroke({
  d,
  draw,
  index,
}: {
  d: string;
  draw: MotionValue<number>;
  index: number;
}) {
  const start = (index / LINE_ART.strokes.length) * (1 - STROKE_WINDOW);
  const pathLength = useTransform(draw, [start, start + STROKE_WINDOW], [0, 1]);
  return (
    <motion.path
      d={d}
      stroke="currentColor"
      strokeWidth={1}
      style={{ pathLength }}
    />
  );
}
